import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import {
  Captions,
  LoaderCircle,
  Maximize,
  Minimize,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { tempsLisible, tempsParle, vitesseLisible } from '../../lib/temps-video';

/**
 * Lecteur vidéo HTML5 de la plateforme (TF-444) : leçons de cours et replays.
 *
 * Les contrôles sont ceux de l'écran, pas ceux du navigateur : la vitesse de
 * lecture et les sous-titres s'y trouvent partout, alors que Firefox et
 * l'iPhone les cachent ou ne les proposent pas. Les répliques sont dessinées
 * au-dessus de la barre de contrôle ; dans le plein écran natif de l'iPhone,
 * le seul qu'il accepte pour une vidéo, la piste passe au rendu du système.
 * Vitesse et sous-titres choisis sont retenus sur l'appareil.
 *
 * La transcription de la leçon (TF-445) et les notes de l'apprenant (TF-446)
 * le pilotent : elles suivent la position de lecture (`onTemps`) et y
 * envoient la vidéo (`allerA`) ; une note ouverte depuis « Mes notes » fait
 * commencer la lecture à son moment (`depart`). La reprise au dernier point
 * de visionnage (TF-449) suit la position (`onTemps`) et ses lancements,
 * pauses et fins (`onEtat`), et fait aussi commencer la lecture (`depart`).
 */

export const VITESSES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;
const SAUT_SECONDES = 10;
const MASQUAGE_MS = 3000;
const CLE_PREFERENCES = 'tf:lecteur-video';

export interface PisteSousTitres {
  source: string;
  langue: string;
  libelle: string;
}

interface Preferences {
  vitesse: number;
  sousTitres: boolean;
}

function lirePreferences(): Preferences {
  try {
    const lues = JSON.parse(window.localStorage.getItem(CLE_PREFERENCES) ?? 'null') as Partial<Preferences> | null;
    const vitesse = VITESSES.find((candidate) => candidate === lues?.vitesse) ?? 1;
    return { vitesse, sousTitres: lues?.sousTitres === true };
  } catch {
    return { vitesse: 1, sousTitres: false };
  }
}

function ecrirePreferences(preferences: Preferences): void {
  try {
    window.localStorage.setItem(CLE_PREFERENCES, JSON.stringify(preferences));
  } catch {
    // Navigation privée ou stockage bloqué : le choix vaut pour cette lecture.
  }
}

function messageErreurMedia(code: number | undefined): string | null {
  if (code === 1) return null;
  if (code === 2) return 'La lecture s’est interrompue : vérifiez votre connexion, puis relancez la vidéo.';
  if (code === 3) return 'Cette vidéo est illisible sur cet appareil.';
  return 'La vidéo ne peut pas être lue : elle n’est plus disponible, ou son format n’est pas lu par cet appareil.';
}

type VideoAppareil = HTMLVideoElement & { webkitEnterFullscreen?: () => void };
type ConteneurAppareil = HTMLDivElement & { webkitRequestFullscreen?: () => Promise<void> | void };
type DocumentAppareil = Document & {
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
  webkitExitFullscreen?: () => Promise<void> | void;
};

function dureeDe(element: HTMLVideoElement): number | null {
  return Number.isFinite(element.duration) && element.duration > 0 ? element.duration : null;
}

function elementPleinEcran(): Element | null {
  const doc = document as DocumentAppareil;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

/** Ce que l'écran autour du lecteur peut lui demander. */
export interface CommandesLecteur {
  /** Va à `secondes` et lance la lecture. */
  allerA(secondes: number): void;
}

interface ProprietesLecteur {
  source: string;
  titre: string;
  sousTitres?: PisteSousTitres | null;
  autoPlay?: boolean;
  /** Durée annoncée par l'API, affichée avant que la vidéo ait chargé ses métadonnées. */
  dureeConnue?: number | null;
  onFin?: () => void;
  /** Position de lecture, à chaque avancée, et durée lue (`null` tant qu'elle ne l'est pas). */
  onTemps?: (secondes: number, duree: number | null) => void;
  /** Lecture lancée, mise en pause ou arrivée au bout, à telle position. */
  onEtat?: (evenement: 'lecture' | 'pause' | 'fin', secondes: number, duree: number | null) => void;
  /** Position où la lecture commence, en secondes (une note de « Mes notes », la reprise). */
  depart?: number | null;
}

const CLASSE_BOUTON =
  'inline-flex h-10 min-w-10 shrink-0 items-center justify-center rounded-lg px-2 text-white transition hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-codialis-mint';

export const LecteurVideo = forwardRef<CommandesLecteur, ProprietesLecteur>(function LecteurVideo(
  { source, titre, sousTitres = null, autoPlay = false, dureeConnue = null, onFin, onTemps, onEtat, depart = null },
  commandes,
): JSX.Element {
  const conteneur = useRef<ConteneurAppareil>(null);
  const video = useRef<VideoAppareil>(null);
  const piste = useRef<HTMLTrackElement>(null);
  const surface = useRef<HTMLDivElement>(null);
  const minuterie = useRef<number | undefined>(undefined);
  const departApplique = useRef(false);
  const [enLecture, setEnLecture] = useState(false);
  const [temps, setTemps] = useState(0);
  const [duree, setDuree] = useState(dureeConnue ?? 0);
  const [tampon, setTampon] = useState(0);
  const [attente, setAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<Preferences>(lirePreferences);
  const [muet, setMuet] = useState(false);
  const [volume, setVolume] = useState(1);
  const [pleinEcran, setPleinEcran] = useState(false);
  const [pleinEcranNatif, setPleinEcranNatif] = useState(false);
  const [choixVitesse, setChoixVitesse] = useState(false);
  const [controlesVisibles, setControlesVisibles] = useState(true);
  const [tactile, setTactile] = useState(false);
  const [repliques, setRepliques] = useState<string[]>([]);
  const [annonce, setAnnonce] = useState('');
  const sousTitresActifs = preferences.sousTitres && sousTitres !== null;

  const changerPreferences = useCallback((modification: Partial<Preferences>) => {
    setPreferences((actuelles) => {
      const suivantes = { ...actuelles, ...modification };
      ecrirePreferences(suivantes);
      return suivantes;
    });
  }, []);

  useEffect(() => {
    departApplique.current = false;
    setErreur(null);
    setTemps(0);
    setTampon(0);
    setRepliques([]);
    setDuree(dureeConnue ?? 0);
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const element = video.current;
    if (!element) return;
    element.defaultPlaybackRate = preferences.vitesse;
    element.playbackRate = preferences.vitesse;
  }, [preferences.vitesse, source]);

  // La piste reste chargée (« hidden ») pour que les répliques soient
  // dessinées par l'écran ; le plein écran natif de l'iPhone les dessine
  // lui-même (« showing »).
  useEffect(() => {
    const track = piste.current?.track;
    if (!track) return;
    track.mode = pleinEcranNatif && sousTitresActifs ? 'showing' : 'hidden';
    const lire = () =>
      setRepliques(
        Array.from(track.activeCues ?? []).map((replique) =>
          ((replique as VTTCue).text ?? '').replace(/<[^>]+>/g, ''),
        ),
      );
    lire();
    track.addEventListener('cuechange', lire);
    return () => track.removeEventListener('cuechange', lire);
  }, [sousTitres?.source, sousTitresActifs, pleinEcranNatif]);

  useEffect(() => {
    const suivre = () => setPleinEcran(elementPleinEcran() === conteneur.current);
    document.addEventListener('fullscreenchange', suivre);
    document.addEventListener('webkitfullscreenchange', suivre);
    return () => {
      document.removeEventListener('fullscreenchange', suivre);
      document.removeEventListener('webkitfullscreenchange', suivre);
    };
  }, []);

  useEffect(() => {
    const element = video.current;
    if (!element) return;
    const debut = () => setPleinEcranNatif(true);
    const fin = () => setPleinEcranNatif(false);
    element.addEventListener('webkitbeginfullscreen', debut);
    element.addEventListener('webkitendfullscreen', fin);
    return () => {
      element.removeEventListener('webkitbeginfullscreen', debut);
      element.removeEventListener('webkitendfullscreen', fin);
    };
  }, []);

  const reveler = useCallback(() => {
    setControlesVisibles(true);
    window.clearTimeout(minuterie.current);
    if (enLecture && !choixVitesse) {
      minuterie.current = window.setTimeout(() => setControlesVisibles(false), MASQUAGE_MS);
    }
  }, [enLecture, choixVitesse]);

  useEffect(() => {
    reveler();
    return () => window.clearTimeout(minuterie.current);
  }, [reveler]);

  const basculerLecture = useCallback(() => {
    const element = video.current;
    if (!element) return;
    if (element.paused || element.ended) {
      setErreur(null);
      try {
        void Promise.resolve(element.play()).catch(() => undefined);
      } catch {
        // Lecture refusée par le navigateur : le bouton reste « Lire ».
      }
    } else {
      element.pause();
    }
  }, []);

  useImperativeHandle(
    commandes,
    () => ({
      allerA(secondes: number) {
        const element = video.current;
        if (!element) return;
        const fin = Number.isFinite(element.duration) ? element.duration : Number.POSITIVE_INFINITY;
        element.currentTime = Math.min(fin, Math.max(0, secondes));
        setTemps(element.currentTime);
        if (element.paused || element.ended) {
          setErreur(null);
          void Promise.resolve(element.play()).catch(() => undefined);
        }
      },
    }),
    [],
  );

  const deplacer = useCallback((ecart: number) => {
    const element = video.current;
    if (!element) return;
    const fin = Number.isFinite(element.duration) ? element.duration : Number.POSITIVE_INFINITY;
    element.currentTime = Math.min(fin, Math.max(0, element.currentTime + ecart));
    setTemps(element.currentTime);
  }, []);

  const basculerSousTitres = useCallback(() => {
    if (!sousTitres) return;
    const actifs = !preferences.sousTitres;
    changerPreferences({ sousTitres: actifs });
    setAnnonce(actifs ? 'Sous-titres activés' : 'Sous-titres désactivés');
  }, [sousTitres, preferences.sousTitres, changerPreferences]);

  const basculerMuet = useCallback(() => {
    const element = video.current;
    if (!element) return;
    element.muted = !element.muted;
    setMuet(element.muted);
  }, []);

  const basculerPleinEcran = useCallback(() => {
    const doc = document as DocumentAppareil;
    const boite = conteneur.current;
    const element = video.current;
    if (!boite || !element) return;
    if (elementPleinEcran()) {
      void Promise.resolve(doc.exitFullscreen ? doc.exitFullscreen() : doc.webkitExitFullscreen?.()).catch(
        () => undefined,
      );
      return;
    }
    if (doc.fullscreenEnabled && boite.requestFullscreen) {
      void boite.requestFullscreen().catch(() => undefined);
    } else if (doc.webkitFullscreenEnabled && boite.webkitRequestFullscreen) {
      void Promise.resolve(boite.webkitRequestFullscreen()).catch(() => undefined);
    } else {
      element.webkitEnterFullscreen?.();
    }
  }, []);

  const choisirVitesse = (vitesse: number) => {
    changerPreferences({ vitesse });
    setAnnonce(`Vitesse de lecture ${vitesseLisible(vitesse)}`);
    setChoixVitesse(false);
  };

  const auClavier = (evenement: KeyboardEvent<HTMLDivElement>) => {
    if (evenement.altKey || evenement.ctrlKey || evenement.metaKey) return;
    const cible = evenement.target as HTMLElement;
    const surCurseur = cible instanceof HTMLInputElement && cible.type === 'range';
    const surBouton = cible.tagName === 'BUTTON';
    const touche = evenement.key.toLowerCase();
    if (touche === 'escape' && choixVitesse) {
      setChoixVitesse(false);
    } else if ((touche === ' ' && !surBouton) || touche === 'k') {
      basculerLecture();
    } else if (touche === 'arrowleft' && !surCurseur) {
      deplacer(-SAUT_SECONDES);
    } else if (touche === 'arrowright' && !surCurseur) {
      deplacer(SAUT_SECONDES);
    } else if (touche === 'f') {
      basculerPleinEcran();
    } else if (touche === 'c') {
      basculerSousTitres();
    } else if (touche === 'm') {
      basculerMuet();
    } else {
      return;
    }
    evenement.preventDefault();
    reveler();
  };

  // Au doigt, toucher l'image montre ou cache la barre : c'est `toucherSurface`
  // qui en décide, au relâcher. Un doigt posé sur un contrôle la garde visible.
  const surPointeur = (evenement: PointerEvent<HTMLDivElement>) => {
    const auDoigt = evenement.pointerType === 'touch';
    setTactile(auDoigt);
    if (!auDoigt || evenement.target !== surface.current) reveler();
  };

  const toucherSurface = () => {
    if (!tactile) {
      basculerLecture();
    } else if (controlesVisibles && enLecture) {
      window.clearTimeout(minuterie.current);
      setControlesVisibles(false);
    } else {
      reveler();
    }
  };

  const fin = duree > 0 ? duree : 0;
  const avancement = fin > 0 ? Math.min(100, (temps / fin) * 100) : 0;
  const charge = fin > 0 ? Math.min(100, (tampon / fin) * 100) : 0;
  const barreVisible = controlesVisibles || !enLecture;
  const boutonCentral = !erreur && (!enLecture || (tactile && controlesVisibles));

  return (
    <div
      ref={conteneur}
      role="region"
      aria-label={`Lecteur vidéo : ${titre}`}
      tabIndex={-1}
      onKeyDown={auClavier}
      onPointerMove={surPointeur}
      onPointerDown={surPointeur}
      className={`relative select-none overflow-hidden bg-black text-white outline-none ${
        pleinEcran ? 'flex h-full w-full items-center' : ''
      } ${barreVisible ? '' : 'cursor-none'}`}
    >
      <video
        ref={video}
        src={source}
        autoPlay={autoPlay}
        playsInline
        preload="metadata"
        onPlay={(evenement) => {
          setEnLecture(true);
          onEtat?.('lecture', evenement.currentTarget.currentTime, dureeDe(evenement.currentTarget));
        }}
        onPause={(evenement) => {
          setEnLecture(false);
          // La fin d'une vidéo passe aussi par une pause : `onEnded` la dit.
          if (!evenement.currentTarget.ended) {
            onEtat?.('pause', evenement.currentTarget.currentTime, dureeDe(evenement.currentTarget));
          }
        }}
        onEnded={(evenement) => {
          setEnLecture(false);
          onEtat?.('fin', evenement.currentTarget.currentTime, dureeDe(evenement.currentTarget));
          onFin?.();
        }}
        onTimeUpdate={(evenement) => {
          setTemps(evenement.currentTarget.currentTime);
          onTemps?.(evenement.currentTarget.currentTime, dureeDe(evenement.currentTarget));
        }}
        onLoadedMetadata={(evenement) => {
          const element = evenement.currentTarget;
          if (Number.isFinite(element.duration)) setDuree(element.duration);
          element.defaultPlaybackRate = preferences.vitesse;
          element.playbackRate = preferences.vitesse;
          if (depart !== null && depart > 0 && !departApplique.current) {
            departApplique.current = true;
            element.currentTime = Number.isFinite(element.duration) ? Math.min(element.duration, depart) : depart;
            setTemps(element.currentTime);
          }
        }}
        onDurationChange={(evenement) => {
          if (Number.isFinite(evenement.currentTarget.duration)) setDuree(evenement.currentTarget.duration);
        }}
        onProgress={(evenement) => {
          const plages = evenement.currentTarget.buffered;
          if (plages.length > 0) setTampon(plages.end(plages.length - 1));
        }}
        onWaiting={() => setAttente(true)}
        onPlaying={() => setAttente(false)}
        onCanPlay={() => setAttente(false)}
        onVolumeChange={(evenement) => {
          setMuet(evenement.currentTarget.muted);
          setVolume(evenement.currentTarget.volume);
        }}
        onError={(evenement) => setErreur(messageErreurMedia(evenement.currentTarget.error?.code))}
        className={pleinEcran ? 'h-full w-full object-contain' : 'aspect-video w-full'}
      >
        {sousTitres && (
          <track ref={piste} kind="subtitles" src={sousTitres.source} srcLang={sousTitres.langue} label={sousTitres.libelle} />
        )}
      </video>

      <div
        ref={surface}
        className="absolute inset-0"
        onClick={toucherSurface}
        onDoubleClick={() => !tactile && basculerPleinEcran()}
        aria-hidden
      />

      {sousTitresActifs && repliques.length > 0 && (
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-x-0 flex flex-col items-center gap-1 px-4 text-center transition-all ${
            barreVisible ? 'bottom-24' : 'bottom-6'
          }`}
        >
          {repliques.map((replique, rang) => (
            <span
              key={rang}
              className="max-w-[90%] whitespace-pre-line rounded bg-black/75 px-2 py-0.5 text-sm leading-snug sm:text-base lg:text-xl"
            >
              {replique}
            </span>
          ))}
        </div>
      )}

      {attente && enLecture && !erreur && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <LoaderCircle className="h-10 w-10 animate-spin text-white/80" aria-hidden />
        </div>
      )}

      {boutonCentral && (
        <button
          type="button"
          onClick={basculerLecture}
          aria-label={enLecture ? 'Mettre en pause' : 'Lire la vidéo'}
          className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-codialis-navy shadow-2xl transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-codialis-mint sm:h-20 sm:w-20"
        >
          {enLecture ? <Pause className="h-7 w-7 sm:h-8 sm:w-8" aria-hidden /> : <Play className="ml-1 h-7 w-7 sm:h-8 sm:w-8" aria-hidden />}
        </button>
      )}

      {erreur && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-4 text-center text-sm" role="alert">
          <p className="max-w-md leading-relaxed">{erreur}</p>
        </div>
      )}

      {choixVitesse && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/85 p-3">
          <div className="flex w-full max-w-sm items-center justify-between">
            <span id="titre-vitesse" className="text-sm font-semibold">
              Vitesse de lecture
            </span>
            <button type="button" onClick={() => setChoixVitesse(false)} aria-label="Fermer" className={CLASSE_BOUTON}>
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>
          <div role="radiogroup" aria-labelledby="titre-vitesse" className="grid w-full max-w-sm grid-cols-4 gap-2">
            {VITESSES.map((vitesse) => (
              <button
                key={vitesse}
                type="button"
                role="radio"
                aria-checked={preferences.vitesse === vitesse}
                autoFocus={preferences.vitesse === vitesse}
                onClick={() => choisirVitesse(vitesse)}
                className={`h-10 rounded-lg text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-codialis-mint ${
                  preferences.vitesse === vitesse
                    ? 'bg-codialis-mint text-codialis-navy'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {vitesseLisible(vitesse)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-2 pb-1 pt-8 transition-opacity sm:px-4 sm:pb-2 ${
          barreVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <div className="relative flex h-5 items-center rounded-full [&:has(:focus-visible)]:ring-2 [&:has(:focus-visible)]:ring-codialis-mint [@media(pointer:coarse)]:h-8">
          <div className="relative h-1.5 w-full rounded-full bg-white/25">
            <div className="absolute inset-y-0 left-0 rounded-full bg-white/40" style={{ width: `${charge}%` }} />
            <div className="absolute inset-y-0 left-0 rounded-full bg-codialis-mint" style={{ width: `${avancement}%` }} />
            <div
              className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-codialis-mint ring-4 ring-white/20"
              style={{ left: `${avancement}%` }}
            />
          </div>
          <input
            type="range"
            min={0}
            max={fin || 0}
            step={1}
            value={Math.min(temps, fin)}
            disabled={fin === 0}
            onChange={(evenement) => {
              const element = video.current;
              if (!element) return;
              element.currentTime = Number(evenement.target.value);
              setTemps(element.currentTime);
            }}
            aria-label="Position dans la vidéo"
            aria-valuetext={`${tempsParle(temps)} sur ${tempsParle(fin)}`}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-default"
          />
        </div>

        <div className="flex items-center gap-0.5 sm:gap-1">
          <button
            type="button"
            onClick={basculerLecture}
            aria-label={enLecture ? 'Mettre en pause' : 'Lire'}
            className={CLASSE_BOUTON}
          >
            {enLecture ? <Pause className="h-5 w-5" aria-hidden /> : <Play className="h-5 w-5" aria-hidden />}
          </button>
          <button
            type="button"
            onClick={() => deplacer(-SAUT_SECONDES)}
            aria-label={`Reculer de ${SAUT_SECONDES} secondes`}
            className={CLASSE_BOUTON}
          >
            <RotateCcw className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => deplacer(SAUT_SECONDES)}
            aria-label={`Avancer de ${SAUT_SECONDES} secondes`}
            className={CLASSE_BOUTON}
          >
            <RotateCw className="h-5 w-5" aria-hidden />
          </button>
          <span className="whitespace-nowrap px-1 font-mono text-[11px] text-white/90 sm:text-xs">
            {tempsLisible(temps)} / {tempsLisible(fin)}
          </span>
          <div className="min-w-0 flex-1" />
          <button
            type="button"
            onClick={() => setChoixVitesse(true)}
            aria-haspopup="dialog"
            aria-expanded={choixVitesse}
            aria-label={`Vitesse de lecture : ${vitesseLisible(preferences.vitesse)}`}
            className={`${CLASSE_BOUTON} text-xs font-semibold`}
          >
            {vitesseLisible(preferences.vitesse)}
          </button>
          {sousTitres && (
            <button
              type="button"
              onClick={basculerSousTitres}
              aria-pressed={sousTitresActifs}
              aria-label="Sous-titres"
              title={sousTitresActifs ? 'Masquer les sous-titres' : 'Afficher les sous-titres'}
              className={`${CLASSE_BOUTON} ${sousTitresActifs ? 'text-codialis-mint' : ''}`}
            >
              <Captions className="h-5 w-5" aria-hidden />
            </button>
          )}
          <button
            type="button"
            onClick={basculerMuet}
            aria-pressed={muet}
            aria-label="Couper le son"
            className={`${CLASSE_BOUTON} hidden sm:inline-flex [@media(pointer:coarse)]:hidden`}
          >
            {muet || volume === 0 ? <VolumeX className="h-5 w-5" aria-hidden /> : <Volume2 className="h-5 w-5" aria-hidden />}
          </button>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={muet ? 0 : volume}
            onChange={(evenement) => {
              const element = video.current;
              if (!element) return;
              element.volume = Number(evenement.target.value);
              element.muted = element.volume === 0;
            }}
            aria-label="Volume"
            className="hidden w-20 accent-codialis-mint md:block [@media(pointer:coarse)]:hidden"
          />
          <button
            type="button"
            onClick={basculerPleinEcran}
            aria-label={pleinEcran ? 'Quitter le plein écran' : 'Plein écran'}
            className={CLASSE_BOUTON}
          >
            {pleinEcran ? <Minimize className="h-5 w-5" aria-hidden /> : <Maximize className="h-5 w-5" aria-hidden />}
          </button>
        </div>
      </div>

      <span className="sr-only" aria-live="polite">
        {annonce}
      </span>
    </div>
  );
});
